import React, { useState } from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { CheckCircle, XCircle, RefreshCw, AlertTriangle } from 'lucide-react';
import { supabase } from '@/integrations/supabase/client';
import { toast } from 'sonner';

interface CredentialDiagnostic {
  timestamp: string;
  redditKeysFound: string[];
  credentialCheck: Array<{
    key: string;
    hasValue: boolean;
    length: number;
    preview: string;
  }>;
  finalResult: {
    clientId: {
      hasValue: boolean;
      length: number;
      preview: string;
    };
    clientSecret: {
      hasValue: boolean;
      length: number;
      preview: string;
    };
  };
  wouldPass: boolean;
  allEnvKeys: string[];
}

export const RedditCredentialsDiagnostic = () => {
  const [diagnostic, setDiagnostic] = useState<CredentialDiagnostic | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const runDiagnostic = async () => {
    setLoading(true);
    setError(null);
    
    try {
      const { data, error: funcError } = await supabase.functions.invoke('reddit-credentials-diagnostic');
      
      if (funcError) {
        throw new Error(funcError.message || 'Failed to run diagnostic');
      }
      
      setDiagnostic(data);
      
      if (data.wouldPass) {
        toast.success('Reddit credentials are properly configured!');
      } else {
        toast.error('Reddit credentials are missing or incomplete');
      }
    } catch (err) {
      const errorMsg = err instanceof Error ? err.message : 'Failed to run diagnostic';
      setError(errorMsg);
      toast.error(errorMsg);
    } finally {
      setLoading(false);
    }
  };

  const testRedditOAuth = async () => {
    setLoading(true);
    
    try {
      const { data, error: funcError } = await supabase.functions.invoke('oauth-initiate', {
        body: { platform: 'reddit' }
      });
      
      if (funcError) {
        throw new Error(funcError.message || 'OAuth initiation failed');
      }
      
      if (data?.authUrl) {
        toast.success('Reddit OAuth is working! Redirect URL generated.');
      } else {
        toast.error('OAuth initiation succeeded but no auth URL returned');
      }
    } catch (err) {
      const errorMsg = err instanceof Error ? err.message : 'OAuth test failed';
      toast.error(`Reddit OAuth Test Failed: ${errorMsg}`);
    } finally {
      setLoading(false);
    }
  };

  return (
    <Card className="w-full">
      <CardHeader>
        <CardTitle className="flex items-center justify-between">
          Reddit OAuth Diagnostics
          <div className="flex gap-2">
            <Button 
              onClick={runDiagnostic} 
              disabled={loading}
              size="sm"
              variant="outline"
            >
              {loading ? (
                <RefreshCw className="h-4 w-4 mr-2 animate-spin" />
              ) : (
                <RefreshCw className="h-4 w-4 mr-2" />
              )}
              Check Credentials
            </Button>
            <Button 
              onClick={testRedditOAuth} 
              disabled={loading}
              size="sm"
            >
              Test OAuth
            </Button>
          </div>
        </CardTitle>
        <CardDescription>
          Verify Reddit OAuth credentials are properly configured in Supabase
        </CardDescription>
      </CardHeader>

      <CardContent className="space-y-4">
        {error && (
          <div className="border border-destructive/20 rounded-lg p-3 bg-destructive/5">
            <div className="flex items-center space-x-2">
              <XCircle className="h-4 w-4 text-destructive" />
              <span className="text-sm text-destructive">{error}</span>
            </div>
          </div>
        )}

        {diagnostic && (
          <div className="space-y-4">
            <div className="flex items-center space-x-2">
              {diagnostic.wouldPass ? (
                <CheckCircle className="h-5 w-5 text-green-600" />
              ) : (
                <XCircle className="h-5 w-5 text-destructive" />
              )}
              <span className="font-medium">
                {diagnostic.wouldPass ? 'Credentials Configured' : 'Credentials Missing'}
              </span>
            </div>

            <div className="space-y-2">
              <h4 className="font-medium text-sm">Environment Variables Found:</h4>
              {diagnostic.redditKeysFound.length > 0 ? (
                <div className="flex flex-wrap gap-1">
                  {diagnostic.redditKeysFound.map(key => (
                    <Badge key={key} variant="outline" className="text-xs">
                      {key}
                    </Badge>
                  ))}
                </div>
              ) : (
                <p className="text-sm text-muted-foreground">No Reddit environment variables found</p>
              )}
            </div>

            <div className="space-y-2">
              <h4 className="font-medium text-sm">Credential Check Results:</h4>
              <div className="space-y-1">
                {diagnostic.credentialCheck.map(check => (
                  <div key={check.key} className="flex items-center justify-between text-xs">
                    <span className="font-mono">{check.key}:</span>
                    <div className="flex items-center space-x-2">
                      {check.hasValue ? (
                        <>
                          <CheckCircle className="h-3 w-3 text-green-600" />
                          <span>{check.preview} ({check.length} chars)</span>
                        </>
                      ) : (
                        <>
                          <XCircle className="h-3 w-3 text-destructive" />
                          <span>EMPTY</span>
                        </>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            </div>

            <div className="border-t pt-3">
              <h4 className="font-medium text-sm mb-2">Final Resolution:</h4>
              <div className="text-xs space-y-1">
                <div className="flex items-center justify-between">
                  <span>Client ID:</span>
                  {diagnostic.finalResult.clientId.hasValue ? (
                    <span className="text-green-600">✓ {diagnostic.finalResult.clientId.preview}</span>
                  ) : (
                    <span className="text-destructive">✗ Missing</span>
                  )}
                </div>
                <div className="flex items-center justify-between">
                  <span>Client Secret:</span>
                  {diagnostic.finalResult.clientSecret.hasValue ? (
                    <span className="text-green-600">✓ {diagnostic.finalResult.clientSecret.preview}</span>
                  ) : (
                    <span className="text-destructive">✗ Missing</span>
                  )}
                </div>
              </div>
            </div>

            {!diagnostic.wouldPass && (
              <div className="border border-amber-200 rounded-lg p-3 bg-amber-50">
                <div className="flex items-start space-x-2">
                  <AlertTriangle className="h-4 w-4 text-amber-600 mt-0.5" />
                  <div className="text-sm">
                    <p className="font-medium text-amber-800">Required Environment Variables:</p>
                    <ul className="text-amber-700 mt-1 space-y-1">
                      <li>• <code>REDDIT_CLIENT_ID</code> - Your Reddit app client ID</li>
                      <li>• <code>REDDIT_CLIENT_SECRET</code> - Your Reddit app secret</li>
                    </ul>
                    <p className="mt-2 text-xs text-amber-600">
                      Add these in Supabase Dashboard → Project Settings → Edge Functions → Environment Variables
                    </p>
                  </div>
                </div>
              </div>
            )}
          </div>
        )}
      </CardContent>
    </Card>
  );
};